import { appendMissingParentInputs } from './parentInputs.js'
import { resolveDomainEventOrDecision } from './workflowSpec.js'

export const buildDeleteEventMutation = ({ workflow, workflowId, eventRef }) => {
  if (!eventRef) {
    return { error: 'Domain event ref is required' }
  }

  const resolvedEvent = resolveDomainEventOrDecision(eventRef, workflow)
  if (!resolvedEvent) {
    return { error: `Domain event or decision not found for ref: ${eventRef}` }
  }
  const eventId = resolvedEvent.id

  const domainEvents = workflow.domainEvents || []
  const eventIndex = domainEvents.findIndex((e) => e.id === eventId)

  if (eventIndex === -1) {
    return { error: 'Event not found' }
  }

  const event = domainEvents[eventIndex]

  if (domainEvents.length === 1) {
    return { error: 'Cannot delete the only event in the workflow' }
  }

  const childEvents = domainEvents.filter((e) =>
    e.parents && e.parents.includes(eventId)
  )

  const eventParentId = event.parents?.[0] || 'start'
  const eventInputs = []
  const reparentedChildren = []

  for (const child of childEvents) {
    const childIndex = domainEvents.findIndex((e) => e.id === child.id)
    const updatedParents = (child.parents || []).map((p) =>
      p === eventId ? eventParentId : p
    )

    eventInputs.push({
      id: child.id,
      index: childIndex,
      parents: updatedParents,
      expectedVersion: child.version || undefined
    })
    reparentedChildren.push(child.description || child.id)
  }

  for (const parentId of (event.parents || [])) {
    if (parentId !== 'start') {
      const parentEvent = domainEvents.find((e) => e.id === parentId)
      if (parentEvent) {
        const parentIndex = domainEvents.findIndex((e) => e.id === parentId)
        eventInputs.push({
          id: parentId,
          index: parentIndex,
          expectedVersion: parentEvent.version || undefined
        })
      }
    }
  }

  const mutationInput = {
    id: workflowId,
    expectedVersion: workflow.version,
    deletedEvents: [{
      deleteIndex: eventIndex,
      deleteId: eventId,
      deleteVersion: event.version || undefined
    }],
    domainEvents: appendMissingParentInputs(eventInputs, domainEvents),
    updateNote: {
      action: 'deleted event',
      target: event.description || eventId
    }
  }

  const cardCount = (event.cards || []).length

  let message = `Event "${event.description || eventId}" deleted successfully.`
  if (cardCount > 0) {
    message += ` Note: ${cardCount} card(s) were also deleted with this event.`
  }
  if (reparentedChildren.length > 0) {
    const parentName = eventParentId === 'start'
      ? 'start'
      : (domainEvents.find((e) => e.id === eventParentId)?.description || eventParentId)
    message += ` Reparented ${reparentedChildren.length} child event(s) to "${parentName}": ${reparentedChildren.join(', ')}`
  }

  return {
    mutationInput,
    event,
    eventId,
    eventParentId,
    reparentedChildren,
    cardCount,
    message
  }
}
